/**
 * MarketSlot — one of 4 market slots (real tile preview + wildlife disc).
 *
 * Originally derived from the (removed) board-game-playground for visual
 * parity (UI is NOT shared per CLAUDE.md — only `@cloudnest/cascadia-engine` is). Engine
 * (PR-4) resolves `slot.tileDefId` and `slot.wildlifeSpecies`, so the slot
 * renders the actual habitats via the shared `TileFill` util and the real
 * wildlife disc — no more colour-only placeholders nor instance-id modulo
 * guessing.
 *
 * data-testid: cascadia-market-slot-{index}
 * data-action-type: DraftPair
 */

import type { MarketSlot as MarketSlotData } from "@cloudnest/cascadia-engine";
import { TILE_DEFS_BY_ID } from "@cloudnest/cascadia-engine";
import { habitatsOf, TileFill } from "../utils/tile-render";
import { PlaceholderWildlife } from "./placeholders";

export interface MarketSlotProps {
  readonly slot: MarketSlotData;
  readonly isSelected: boolean;
  readonly isDisabled: boolean;
  readonly onTap: (slotIndex: number) => void;
}

const TILE_HEX_SIZE = 14;

export function MarketSlot({
  slot,
  isSelected,
  isDisabled,
  onTap,
}: MarketSlotProps) {
  const isEmpty = slot.tileInstanceId === null;
  const testId = `cascadia-market-slot-${slot.index}`;
  const tileDef = slot.tileDefId ? TILE_DEFS_BY_ID[slot.tileDefId] : undefined;
  const species = slot.wildlifeSpecies;

  return (
    <button
      data-testid={testId}
      data-action-type="DraftPair"
      data-slot-index={slot.index}
      data-tile-def-id={slot.tileDefId ?? ""}
      data-wildlife-species={species ?? ""}
      onClick={() => !isDisabled && !isEmpty && onTap(slot.index)}
      disabled={isDisabled || isEmpty}
      aria-label={
        isEmpty
          ? `Market slot ${slot.index + 1}: empty`
          : `Market slot ${slot.index + 1}: ${slot.tileDefId ?? "tile"} and ${species ?? "token"} pair`
      }
      aria-pressed={isSelected}
      type="button"
      style={{
        minWidth: 64,
        minHeight: 64,
        padding: 8,
        borderRadius: 8,
        border: isSelected
          ? "2px solid var(--cn-cascadia-selection)"
          : "1px solid var(--cn-cascadia-market-slot-border)",
        background: isEmpty
          ? "var(--cn-cascadia-tray-empty-slot)"
          : isSelected
            ? "var(--cn-cascadia-phase-bg)"
            : "var(--cn-cascadia-market-slot)",
        cursor: isDisabled || isEmpty ? "not-allowed" : "pointer",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        opacity: isDisabled ? 0.5 : 1,
        transition: "border-color 0.15s, background 0.15s",
        flex: "1 1 0",
      }}
    >
      {isEmpty ? (
        <span style={{ fontSize: 11, color: "var(--cn-cascadia-label)" }}>
          Empty
        </span>
      ) : (
        <>
          {/* Real tile preview — single or dual habitat fill per engine def. */}
          <svg
            width={TILE_HEX_SIZE * 2}
            height={TILE_HEX_SIZE * 2}
            viewBox={`${-TILE_HEX_SIZE} ${-TILE_HEX_SIZE} ${TILE_HEX_SIZE * 2} ${TILE_HEX_SIZE * 2}`}
            aria-hidden="true"
            data-testid={`${testId}-tile`}
          >
            {tileDef ? (
              <TileFill
                cx={0}
                cy={0}
                size={TILE_HEX_SIZE - 1}
                habitats={habitatsOf(tileDef)}
              />
            ) : null}
          </svg>
          {/* Real wildlife token. */}
          {species ? (
            <svg
              width={28}
              height={28}
              aria-hidden="true"
              data-testid={`${testId}-wildlife`}
            >
              <PlaceholderWildlife
                x={14}
                y={14}
                radius={12}
                species={species}
                isDraftable={!isDisabled}
                isSelected={isSelected}
              />
            </svg>
          ) : null}
          {/* Accepted-species hint row — `tile: ●●●` per wireframe
              §"Market visual fidelity (iteration 2)". The species listed
              here are the ones the tile itself will accept later for
              PlaceWildlife, distinct from the paired wildlife shown above. */}
          {tileDef && tileDef.wildlifeOptions.length > 0 ? (
            <div
              data-testid={`${testId}-accepts`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 3,
                fontSize: 9,
                lineHeight: 1,
                color: "var(--cn-cascadia-label)",
              }}
            >
              <span aria-hidden="true">tile:</span>
              <svg
                width={tileDef.wildlifeOptions.length * 9}
                height={8}
                aria-hidden="true"
              >
                {tileDef.wildlifeOptions.map((opt, idx) => (
                  <PlaceholderWildlife
                    key={opt}
                    x={4 + idx * 9}
                    y={4}
                    radius={3.5}
                    species={opt}
                  />
                ))}
              </svg>
            </div>
          ) : null}
        </>
      )}
    </button>
  );
}
